import { useMemo, useState } from 'react';
import {
  Drawer,
  Button,
  Tooltip,
  Modal,
  message,
  Image,
  Table,
  Input,
  Grid,
  Typography,
  Space,
  Empty,
  Skeleton,
  Card,
  Tag,
  Avatar,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  LinkOutlined,
  ThunderboltOutlined,
  UserOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CreateCharger from './CreateCharger';
import {
  useDeleteCharger,
  useFetchSingleLocation,
  useGetChargersByLocation,
  useStopChargingModal,
} from '../Hooks';
import { useOperatorStore } from '../../../store/useOperatorStore';
import { useQueryClient } from '@tanstack/react-query';
import { useIsEditAllowedWithOperator, safeT } from '../../../utils/utils';
import OcppCommandDrawer from './OcppCommandDrawer';
import TransactionDetailsModal from '../../../components/Modals/TransactionDetailsModal';
import { CSS2, Default, GBT, Type2 } from '../../../components/Icons';
import SingleChargerDrawer from './SingleChargerDrawer';
import PropTypes from 'prop-types';
import useDebounce from '../../../utils/debounce';
import { getErrorMessage } from '../../../utils/errorHandler';

const { Title } = Typography;
const { useBreakpoint } = Grid;

/**
 * LocationSingleDrawer — polished UX
 * - Clear section grouping (Overview, Contact, Business, Address, Amenities)
 * - Responsive two-column layout with Cards
 * - Sticky header with status & quick actions
 * - Debounced search; fixed offset math for pagination
 * - Defensive rendering for partial data
 */
const LocationSingleDrawer = ({ isOpen, onClose, selectedLocation, OPId }) => {
  const [openCharger, setOpenCharger] = useState(false);
  const { t } = useTranslation('chargerPage');
  const { t: tModal } = useTranslation('location');

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 300);

  const { operatorId } = useOperatorStore();
  const [selectedChargerId, setSelectedChargerId] = useState(null);
  const isEditAllowed = useIsEditAllowedWithOperator();
  const [isOcppDrawerOpen, setOcppDrawerOpen] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chargerToDelete, setChargerToDelete] = useState(null);
  const [isTransactionModalOpen, setTransactionModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [stopChargingModalOpen, setStopChargingModalOpen] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const queryClient = useQueryClient();

  // --- Queries --------------------------------------------------------------
  const {
    data: location,
    isLoading: isLocationLoading,
    error: locationError,
    isError: isLocationError,
    refetch,
  } = useFetchSingleLocation({
    id: selectedLocation?._id || selectedLocation,
    operatorId: operatorId || OPId,
  });

  const { data: chargers, isLoading: isChargerLoading } = useGetChargersByLocation({
    locationId: selectedLocation?._id || selectedLocation,
    operatorId: operatorId || OPId,
    offset: currentPage, // real offset
    limit: pageSize,
    search: debouncedSearchText,
  });

  const { mutate: deleteCharger, isPending } = useDeleteCharger({ operatorId });
  const { mutate: stopCharging, isPending: isStopChargingLoading } = useStopChargingModal();

  // --- Actions --------------------------------------------------------------
  const confirmStopCharging = () => {
    stopCharging(
      {
        operatorId,
        serialNumber: stopChargingModalOpen?.serialNumber,
        ocppTransactionId: stopChargingModalOpen?.ocppTransactionId,
      },
      {
        onSuccess: () => {
          message.success(t('messages.transactionStopped'));
          setStopChargingModalOpen(null);
          refetch();
        },
        onError: (error) => message.error(getErrorMessage(error, t('messages.transactionStopError'))),
      }
    );
  };

  const confirmDeleteCharger = () => {
    deleteCharger(chargerToDelete, {
      onSuccess: () => {
        message.success(t('messages.chargerDeleted'));
        queryClient.invalidateQueries(['GET_Charger_location']);
        setDeleteModalOpen(false);
        setChargerToDelete(null);
      },
      onError: (error) => message.error(getErrorMessage(error, t('messages.deleteFailed'))),
    });
  };

  const handleCloseTransactionModal = () => {
    setSelectedTransactionId(null);
    setTransactionModalOpen(false);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  // --- Derived data ---------------------------------------------------------
  const normalizedLocation = Array.isArray(location) ? location[0] : location;

  const contactPersonValue = useMemo(() => {
    const f = normalizedLocation?.contactPersonFname || '';
    const l = normalizedLocation?.contactPersonLname || '';
    const full = `${f} ${l}`.trim();
    return full || 'N/A';
  }, [normalizedLocation]);

  // Get coordinates from location or first charger
  const locationCoordinates = useMemo(() => {
    if (normalizedLocation?.coordinates && Array.isArray(normalizedLocation.coordinates) && normalizedLocation.coordinates.length >= 2) {
      return normalizedLocation.coordinates;
    }
    // Fallback: try to get from first charger
    const firstCharger = chargers?.paginatedData?.[0];
    if (firstCharger?.location?.coordinates && Array.isArray(firstCharger.location.coordinates) && firstCharger.location.coordinates.length >= 2) {
      return firstCharger.location.coordinates;
    }
    return null;
  }, [normalizedLocation, chargers]);


  

  const connectorIcon = (c) => {
    const status = c?.status || 'Unknown';
    const map = {
      TYPE2: <Type2 className="size-8 transition-transform hover:scale-110" status={status} />,
      CCS2: <CSS2 className="size-8 transition-transform hover:scale-110" status={status} />,
      GBT: <GBT className="size-7 transition-transform hover:scale-110" status={status} />,
    };
    return map[c?.connectorType] || <Default className="size-7 transition-transform hover:scale-110" status={status} />;
  };

  const columns = useMemo(
    () => [
      {
        title: safeT('chargerPage:table.name', 'DEVICE NAME'),
        dataIndex: 'name',
        key: 'name',
        width: 250,
        align: 'left',
        render: (name, charger) => (
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => setSelectedCharger(charger?._id)}
          >
            <div className="flex flex-col">
              <span className="group-hover:text-primary transition-colors leading-tight">
                {name}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: safeT('chargerPage:table.type', 'TYPE'),
        dataIndex: 'powerType',
        key: 'powerType',
        align: 'center',
        render: (type) => (
          <Tag color={type === 'DC' ? 'volcano' : 'green'}>
            {type || '—'}
          </Tag>
        ),
      },
      {
        title: safeT('chargerPage:table.connectors', 'CONNECTORS'),
        key: 'connectors',
        align: 'center',
        render: (_, charger) =>
          charger?.connectors?.length ? (
            <Space size={10} wrap className="justify-center items-center">
              {charger.connectors.map((c) => (
                <Tooltip
                  key={c?.connectorId}
                  title={
                    <div className="text-xs">
                      <div className="font-bold uppercase mb-1">{c?.connectorType || tModal('location.unknown')}</div>
                      <div>{tModal('location.connectorIdLabel')}: {c?.connectorId || 'N/A'}</div>
                      <div>{tModal('location.statusLabel')}: <span className="italic">{t(c?.status || 'Unknown')}</span></div>
                    </div>
                  }
                >
                  <span className="inline-flex items-center justify-center cursor-help">{connectorIcon(c)}</span>
                </Tooltip>
              ))}
            </Space>
          ) : (
            '—'
          ),
      },
      {
        title: safeT('chargerPage:table.action', 'ACTION'),
        key: 'action',
        align: 'center',
        width: 80,
        render: (_, charger) => (
          <Tooltip title={safeT('chargerPage:buttons.viewDetails', 'View Details')}>
            <Button
              type="text"
              size="middle"
              className="text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
              icon={<EyeOutlined style={{ fontSize: '18px' }} />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCharger(charger?._id);
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [t]
  );

  // --- Render ----------------------------------------------------------------
  const headerRight = (
    <Space size={12}>
      {isEditAllowed && (
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          className="rounded-lg shadow-sm hover:shadow-md transition-all font-medium h-9"
          onClick={() => setOpenCharger(true)}
        >
          {t('buttons.addCharger')}
        </Button>
      )}
    </Space>
  );

  return (
    <Drawer
      maskClosable={false}
      title={
        <div className="flex items-center justify-between gap-4 w-full mr-2">
          <div className="flex items-center gap-3 min-w-0">
            <Title level={4} className="!mb-0 truncate font-bold text-gray-800">
              {normalizedLocation?.locationName || t('location.details')}
            </Title>
          </div>
          <div className="flex-shrink-0">{headerRight}</div>
        </div>
      }
      placement="right"
      width={isMobile ? '100%' : '64%'}
      onClose={onClose}
      open={isOpen}
    >
      {/* Details */}
      {isLocationError ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Empty
            description={
              <span className="text-red-500 font-medium">
                {locationError?.response?.data?.message ||
                  locationError?.message ||
                  tModal('locationNotFound')}
              </span>
            }
          />
        </div>
      ) : (
        <Skeleton loading={isLocationLoading} active avatar paragraph={{ rows: 4 }}>
          {/* Hero Section */}
          <Card className="mb-4 overflow-hidden rounded-xl overflow-hidden" bodyStyle={{ padding: 0 }}>
            <div className="flex flex-col md:flex-row">
              {/* Image Section */}
              <div className="md:w-[200px] bg-gray-50 flex items-center justify-center p-3 border-r border-gray-100">
                <div className="relative group overflow-hidden rounded-xl">
                  <Image
                    src={normalizedLocation?.image || '/521410-PJ9L26-638.jpg'}
                    alt="Location"
                    className="object-cover !h-32  w-full rounded-xl shadow-md transition-transform duration-500 group-hover:scale-105"
                    preview
                  />
                </div>
              </div>

              {/* Main Info */}
              <div className="flex-1 p-4 flex flex-col justify-between">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">{tModal('location.contactPerson')}</span>
                    <div className="flex items-center gap-2">
                      <Avatar size="small" icon={<UserOutlined />} className="!bg-primary/10 !text-primary" />
                      <span className="text-sm font-semibold text-gray-700">{contactPersonValue}</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">{tModal('location.emailAddress')}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar size="small" icon={<MailOutlined />} className="!bg-primary/10 !text-primary" />
                      <span className="text-sm font-semibold text-gray-700 truncate">
                        {normalizedLocation?.contactPersonEmailAddress ? (
                          <a href={`mailto:${normalizedLocation.contactPersonEmailAddress}`} className="text-primary hover:underline">
                            {normalizedLocation.contactPersonEmailAddress}
                          </a>
                        ) : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  {locationCoordinates && (
                    <div className="flex items-center gap-2 text-gray-400 group">
                      <EnvironmentOutlined className="text-primary/60" />
                      <span className="text-sm font-mono tracking-tighter">
                        {locationCoordinates[1]?.toFixed(6)}, {locationCoordinates[0]?.toFixed(6)}
                      </span>
                      <a
                        href={`https://www.google.com/maps?q=${locationCoordinates[1]},${locationCoordinates[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary font-bold hover:underline flex items-center gap-0.5"
                      >
                        <LinkOutlined />
                        VIEW ON MAP
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Skeleton>
      )}

      {/* Chargers */}
      {!isLocationError && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex flex-row justify-end gap-4 mb-4">
            <Input
              placeholder={safeT('chargerPage:table.searchByName', 'Search by name...')}
              prefix={<SearchOutlined className="text-gray-300" />}
              allowClear
              className="!max-w-72"
              onChange={(e) => handleSearch(e.target.value)}
              value={searchText}
            />
          </div>

          <Table
            dataSource={chargers?.paginatedData}
            rowKey="_id"
            size="middle"
            className="charger-table"
            pagination={{
              current: currentPage,
              pageSize,
              total: chargers?.totalCount || 0,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
              position: ['bottomCenter'],
            }}
            loading={isChargerLoading}
            scroll={{ x: 'max-content', y: 400 }}
            locale={{
              emptyText: (
                <Empty
                  className="py-12"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={safeT('chargerPage:table.noChargers', 'No chargers found')}
                />
              ),
            }}
            columns={columns}
            onRow={(record) => ({
              onClick: () => setSelectedCharger(record._id),
              className: 'cursor-pointer hover:bg-gray-50/50 transition-colors'
            })}
          />
        </div>
      )}

      {/* Child Modals & Drawers */}
      {openCharger && (
        <CreateCharger
          isOpen={openCharger}
          id={selectedChargerId}
          onOpenChange={() => {
            setOpenCharger(false);
            setSelectedChargerId(null);
          }}
          locationId={normalizedLocation?._id}
          passOperatorId={operatorId}
        />
      )}

      {isOcppDrawerOpen && (
        <OcppCommandDrawer
          isOpen={isOcppDrawerOpen}
          onClose={() => setOcppDrawerOpen(false)}
          charger={selectedCharger}
          refetchLocation={refetch}
        />
      )}

      {isTransactionModalOpen && (
        <TransactionDetailsModal
          open={isTransactionModalOpen}
          onClose={handleCloseTransactionModal}
          id={selectedTransactionId}
        />
      )}

      {selectedCharger && (
        <SingleChargerDrawer
          isOpen={!!selectedCharger}
          onClose={() => setSelectedCharger(null)}
          selectedCharger={selectedCharger}
          passoperatorId={operatorId}
        />
      )}

      {/* Confirm Modals */}
      <Modal
        confirmLoading={isPending && !!chargerToDelete}
        title={<Title level={4} className="mb-0!">{tModal('modal.confirmDeleteTitle')}</Title>}
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onOk={confirmDeleteCharger}
      >
        <p>{tModal('modal.confirmDeleteMessage')}</p>
      </Modal>

      <Modal
        confirmLoading={isStopChargingLoading}
        title={<Title level={4} className="mb-0!">{tModal('modal.confirmStopTitle')}</Title>}
        open={!!stopChargingModalOpen}
        onCancel={() => setStopChargingModalOpen(false)}
        onOk={confirmStopCharging}
      >
        <p>{tModal('modal.confirmStopMessage')}</p>
      </Modal>
    </Drawer>
  );
};

LocationSingleDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedLocation: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ _id: PropTypes.string }),
  ]),
  OPId: PropTypes.string,
};

export default LocationSingleDrawer;
